import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query'

import type { StandaloneFormAccess } from '../../standalone-form-access.types.ts'
import type { StandaloneFormSubmitter } from '../../standalone-form-entry.types.ts'

import {
  nlAovqlKnowledgeAccess,
  nlAovqlKnowledgeSubmitResult,
  type NlAovqlKnowledgeContext,
  type NlAovqlKnowledgeFormResult,
} from './nl-aovql-knowledge-page-state.utils.ts'

import { useTRPC } from '#/client.ts'
import { frontOfficeQueryOptions } from '#/shared/front-office-query-options.ts'
import type { RouterContext } from '#/shared/routing/router-context.ts'

/** Loads the context, for the route loader, and tells whether the customer may take the form now. */
export async function ensureNlAovqlKnowledgeFormAccess(
  { queryClient, trpc }: RouterContext,
  subscriptionId: string,
): Promise<StandaloneFormAccess> {
  const [access] = await Promise.all([
    queryClient.fetchQuery(trpc.nlAovqlKnowledgeForm.getFormAccess.queryOptions({ subscriptionId })),
    // Read by the flow: loaded here so the page does not suspend on it
    queryClient.ensureQueryData(
      trpc.nlAovqlKnowledgeForm.getCurrentState.queryOptions({ subscriptionId }, frontOfficeQueryOptions),
    ),
  ])
  return nlAovqlKnowledgeAccess(access)
}

export function useNlAovqlKnowledgeContext(subscriptionId: string): NlAovqlKnowledgeContext {
  const trpc = useTRPC()
  const { data } = useSuspenseQuery(
    trpc.nlAovqlKnowledgeForm.getCurrentState.queryOptions({ subscriptionId }, frontOfficeQueryOptions),
  )
  return data
}

export function useNlAovqlKnowledgeSubmitter(
  subscriptionId: string,
): StandaloneFormSubmitter<NlAovqlKnowledgeFormResult> {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const { mutate, isPending } = useMutation({
    ...trpc.nlAovqlKnowledgeForm.submitAttempt.mutationOptions(),
    // The gate step and a reload read the attempts left from the context, so it must not serve the previous one
    onSettled: () =>
      queryClient.invalidateQueries({
        queryKey: trpc.nlAovqlKnowledgeForm.getCurrentState.queryKey({ subscriptionId }),
        refetchType: 'all',
      }),
  })

  return {
    submit: ({ definitionVersion, ...submission }, { onSubmitted, onFailed }) =>
      mutate(
        { ...submission, knowledgeFormVersion: definitionVersion },
        {
          onSuccess: (output) => onSubmitted(nlAovqlKnowledgeSubmitResult(output)),
          onError: onFailed,
        },
      ),
    submitting: isPending,
  }
}
